import {Node} from '@tiptap/core'

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    videoEmbed: {
      setVideoEmbed: (options: {src: string}) => ReturnType
    }
  }
}

/**
 * Converts share links to embeddable URLs (same providers the old Quill
 * `video` format and the PHP Summernote dialog supported).
 */
export const toEmbedUrl = (url: string): string | null => {
  const trimmed = url.trim()
  if (!trimmed) return null

  let match = trimmed.match(
    /(?:youtube\.com\/(?:watch\?.*v=|shorts\/|embed\/)|youtu\.be\/)([\w-]{6,})/
  )
  if (match) return `https://www.youtube.com/embed/${match[1]}`

  match = trimmed.match(/vimeo\.com\/(?:video\/)?(\d+)/)
  if (match) return `https://player.vimeo.com/video/${match[1]}`

  match = trimmed.match(/dailymotion\.com\/video\/([\w]+)/)
  if (match) return `https://www.dailymotion.com/embed/video/${match[1]}`

  if (/^https?:\/\//.test(trimmed)) return trimmed
  return null
}

/**
 * Generic iframe video embed. Parses any `<iframe>` — including the
 * `<iframe class="ql-video">` markup stored by the old Quill editor — and
 * renders a plain iframe that displays anywhere without editor CSS.
 */
export const VideoEmbed = Node.create({
  name: 'videoEmbed',
  group: 'block',
  atom: true,
  draggable: true,

  addAttributes() {
    return {
      src: {default: null},
      width: {default: '100%'},
      height: {default: 315},
    }
  },

  parseHTML() {
    return [{tag: 'iframe[src]'}]
  },

  renderHTML({HTMLAttributes}) {
    return [
      'iframe',
      {
        ...HTMLAttributes,
        frameborder: '0',
        allowfullscreen: 'true',
        allow: 'autoplay; fullscreen; picture-in-picture; encrypted-media',
      },
    ]
  },

  addCommands() {
    return {
      setVideoEmbed:
        ({src}) =>
        ({commands}) => {
          const embedUrl = toEmbedUrl(src)
          if (!embedUrl) return false
          return commands.insertContent({type: this.name, attrs: {src: embedUrl}})
        },
    }
  },
})
